import { Box, Skeleton, Stack } from '@mui/material'
import { ImgHTMLAttributes, useEffect, useState } from 'react'
import { Image } from 'utoniq-ui'

import { Resource } from '@/clients/utoniq-core/schema'
import { TheoPlayerImageView } from '@/components/organisms/resourcePlayer/TheoPlayerView'
import { useMessage, useTranslation } from '@/core'
import { useDRMPlayer } from '@/core/resources/useDRMPlayer'

/**
 * @description ImagePlayer
 * 全画面表示の際に使う
 * resourceオブジェクトを受け取り、DRMでないものは画像で返す
 * DRMの場合はDRMプレイヤーを表示し自動再生する
 */

type Props = {
  resource: Resource
  open: boolean
} & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'resource'>

export const ImagePlayer = ({ resource, open, ...imageProps }: Props) => {
  const [showSkeleton, setShowSkeleton] = useState(true)

  if (resource.drm?.encryptingStatus === 'encrypted') {
    return (
      <ImageDRMPlayer
        open={open}
        resource={resource}
        width={imageProps?.width}
      />
    )
  }

  return (
    <Stack
      direction="row"
      justifyContent="center"
      flex={1}
      sx={{
        width: imageProps?.width,
        maxWidth: 600,
        margin: 'auto',
        height: '100%',
        placeItems: 'center',
      }}
    >
      {showSkeleton && (
        <Skeleton
          variant="rectangular"
          width={300}
          height={480}
          style={{
            objectFit: 'contain',
          }}
        />
      )}
      <Image
        src={resource?.compressedUri ?? resource.uri}
        style={{
          width: showSkeleton ? 0 : '100%',
          height: showSkeleton ? 0 : '100%',
          maxWidth: 600,
          objectFit: 'contain',
          visibility: showSkeleton ? 'hidden' : 'visible',
        }}
        onLoad={() => setShowSkeleton(false)}
        {...imageProps}
      />
    </Stack>
  )
}

const ImageDRMPlayer = ({
  resource,
  width,
  open,
}: {
  resource: Resource
  width?: string | number
  open: boolean
}) => {
  const { showError } = useMessage()
  const { t } = useTranslation()
  const { ref, player, accessToken } = useDRMPlayer({
    source: {
      dashUri: resource?.dashUri || '',
      hlsUri: resource?.hlsUri || '',
    },
    opts: {
      mutedAutoplay: 'all',
    },
  })

  useEffect(() => {
    if (player && accessToken) {
      player.play()
    }

    if (open && !accessToken) {
      showError(t('drmAuthorizingError'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, open])

  if (!accessToken) return null

  return (
    <Box
      sx={{
        width,
        maxWidth: 600,
        margin: 'auto',
      }}
    >
      <div>
        <TheoPlayerImageView ref={ref} playable={!!player?.duration} />
      </div>
    </Box>
  )
}
